import groupBy from 'lodash/groupBy';
import useFetch from '../../../../../../hooks/fetch.hook';
import ExerciseApiModel from '../../../../models/exercise.api-model';
import type IExercise from '../../../../../../models/pa-egna-ben/exercise.model';
import type ILegReport from '../../../../../../models/pa-egna-ben/leg-report.model';
import ExerciseHelper, { ExerciseStatus } from '../../../../helpers/exercises.helper';
import { ExerciseLevel } from '../../../../../../models/pa-egna-ben/exercise-level.enum';
import InformationBox from '../../../../Shared/InformationBox';
import type { ILeg } from '../../../../../../models/pa-egna-ben/leg.model';
import { InformationBoxType } from '../../../InformationBox/information-box-type.enum';

function processReceivedData(data: ExerciseApiModel[]): IExercise[] {
  return data?.map((d) => ExerciseApiModel.mapToBusinessModel(d)) ?? [];
}

function Exercises({
  leg,
  selectedExercises,
  legReports,
  className = undefined,
  onChange,
}: {
  leg: ILeg;
  selectedExercises: string[];
  legReports: ILegReport[];
  className?: string;
  onChange: (selectedExercises: string[]) => void;
}) {
  // === STATE ===
  const [{ data: exercisesData }] = useFetch<ExerciseApiModel[]>('/api/paegnaben/teacher/exercises');

  // === VARIABLES ===
  const exercises = exercisesData ? processReceivedData(exercisesData) : undefined;
  const currentLegReport = legReports.find((lr) => lr.leg?.number === leg.number);
  const groupedExercises = groupBy(exercises, (e) => e.level);

  // === HELPER FUNCTIONS ===
  function handleChange(changedId: string) {
    const checked = selectedExercises.indexOf(changedId) === -1;
    if (checked) {
      onChange([...selectedExercises, changedId]);
    } else {
      onChange(selectedExercises.filter((id) => id !== changedId));
    }
  }

  function getExerciseLevelText(level: string) {
    if (level === ExerciseLevel.easy) return 'Lätta övningar';
    if (level === ExerciseLevel.medium) return 'Medelsvåra övningar';
    if (level === ExerciseLevel.hard) return 'Svåra övningar';
    return '';
  }

  function getExerciseLevelSortIndex(level: string) {
    if (level === ExerciseLevel.easy) return 0;
    if (level === ExerciseLevel.medium) return 1;
    if (level === ExerciseLevel.hard) return 2;
    return 3;
  }

  function isDisabled(exercise: IExercise) {
    const exerciseNotYetCompleted = ExerciseHelper.calculateExerciseStatus(exercise, legReports) === ExerciseStatus.notCompleted;
    if (exerciseNotYetCompleted) {
      return false;
    }

    const isCompletedInCurrentLeg = currentLegReport?.completedExercises.includes(exercise.id);
    if (isCompletedInCurrentLeg) {
      return false;
    }

    return true;
  }

  function isChecked(exercise: IExercise) {
    if (selectedExercises.includes(exercise.id)) {
      return true;
    }

    const isCompletedInAnotherLeg =
      ExerciseHelper.calculateExerciseStatus(
        exercise,
        legReports.filter((lr) => lr.id !== currentLegReport?.id)
      ) === ExerciseStatus.completed;
    if (isCompletedInAnotherLeg) {
      return true;
    }

    return false;
  }

  return (
    <div>
      <fieldset className={className ?? ''}>
        <legend>Övningar</legend>
        <InformationBox
          type={InformationBoxType.Default}
          textElement={
            <p className="mb-0">
              Klicka i vilka övningar din klass gjort under tävlingsveckan. En genomförd övning ger 10 poäng. Klassen kan tillgodoräkna sig max 100 poäng (10 övningar) under hela tävlingsperioden.
            </p>
          }
          className="mb-3"
        />
        {Object.keys(groupedExercises)
          .sort((a, b) => getExerciseLevelSortIndex(a) - getExerciseLevelSortIndex(b))
          .map((level, levelCount, levelArray) => (
            <div key={level} className={`${levelCount + 1 === levelArray.length ? '' : 'mb-4'}`}>
              <h3 className="h4">{getExerciseLevelText(level)}</h3>
              {groupedExercises[level].map((exercise, exercisesCount, exercisesArray) => (
                <div key={exercise.id} className={`form-check ${exercisesCount + 1 === exercisesArray.length ? '' : 'mb-3'}`}>
                  <input
                    id={`exercise=${exercise.id}`}
                    type="checkbox"
                    className="form-check-input"
                    checked={isChecked(exercise)}
                    disabled={isDisabled(exercise)}
                    onChange={() => handleChange(exercise.id)}
                  />
                  <label htmlFor={`exercise=${exercise.id}`} className="form-check-label fw-normal">
                    {exercise.name}
                  </label>
                </div>
              ))}
            </div>
          ))}
      </fieldset>
    </div>
  );
}

export default Exercises;
